import { ReactElement, useCallback, useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import { ActionIcon, Tooltip } from '@mantine/core';

import MercuryButton from '@/components/content/button/Button';
import DownloadIcon from '@/assets/icons/content/download.svg';
import { fetchData } from '@/lib/fetch';
import { trackEvent } from '@/lib/posthog/helpers';
import { POSTHOG_EVENTS } from '@/lib/posthog/events';
import { useLocation } from 'react-router-dom';
import {
  compactIncidentActionButtonClassNames,
  type IncidentActionButtonVariant,
} from '../incidentmanagement/components/incidentActionButton';
import { ensureFontsRegistered } from '../issue/pdf/Page';
import type { DocumentProps } from '@react-pdf/renderer';

interface DownloadReportProps {
  document: ReactElement<DocumentProps>;
  asButton?: boolean;
  buttonVariant?: IncidentActionButtonVariant;
  label: string;
  fileName: string;
}

const DownloadReport = ({
  document: pdfDocument,
  asButton,
  buttonVariant,
  label,
  fileName,
}: DownloadReportProps) => {
  const [isGeneratingPdf, setIsGeneratingPdf] = useState<boolean>(false);
  const location = useLocation();
  const { pathname } = location;
  const isCompactOutline = buttonVariant === 'compactOutline';

  const generatePdf = useCallback(async () => {
    setIsGeneratingPdf(true);

    try {
      await ensureFontsRegistered();
      const pdfBlob = pdf(pdfDocument);
      const blob = await pdfBlob.toBlob();
      const pdfBlobUrl = URL.createObjectURL(blob);

      return pdfBlobUrl;
    } finally {
      setIsGeneratingPdf(false);
    }
  }, []);

  const downloadPdf = useCallback(async () => {
    fetchData({ endpoint: `mercury/ping?ctx_source=pdf&ctx_path=${encodeURIComponent(pathname)}` });
    trackEvent(POSTHOG_EVENTS.PDF_EXPORT, { pathname });
    const pdfBlobUrl = await generatePdf();

    if (pdfBlobUrl) {
      const downloadLink = document.createElement('a');
      downloadLink.href = pdfBlobUrl;
      downloadLink.download = fileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  }, [generatePdf]);

  if (asButton) {
    return (
      <MercuryButton
        size={isCompactOutline ? 'xs' : 'sm'}
        variant={isCompactOutline ? 'outline' : undefined}
        color={isCompactOutline ? '#454bd1' : undefined}
        classNames={isCompactOutline ? compactIncidentActionButtonClassNames : undefined}
        loading={isGeneratingPdf}
        onClick={downloadPdf}
        rightSection={
          <DownloadIcon width={isCompactOutline ? 16 : 20} height={isCompactOutline ? 16 : 20} />
        }
      >
        {label}
      </MercuryButton>
    );
  }

  return (
    <Tooltip label={label}>
      <ActionIcon
        onClick={downloadPdf}
        variant="subtle"
        aria-label={label}
        loading={isGeneratingPdf}
      >
        <DownloadIcon />
      </ActionIcon>
    </Tooltip>
  );
};

export default DownloadReport;
